import { UserAnswers, Grant } from '../types';
import { fetchStateGrants } from './stateGrantService';
import { fetchSpecializedGrants } from './specializedGrantService';
import { fetchFederalGrants } from './federalGrantService';

export async function getMatchedGrants(answers: UserAnswers): Promise<Grant[]> {
  try {
    const state = (answers.state as string) || 'CA';

    // Fetch grants from all sources in parallel
    const [stateGrants, specializedGrants, federalGrants] = await Promise.all([
      fetchStateGrants(state),
      fetchSpecializedGrants({ 
        location: state.toLowerCase(),
        focusArea: answers.primaryFocus as string,
        eligibility: answers.organizationType as string
      }),
      fetchFederalGrants({
        state: state,
        category: answers.primaryFocus as string,
        demographics: answers.demographics as string[]
      })
    ]);

    const allGrants = [...stateGrants, ...specializedGrants, ...federalGrants];

    // Filter and score grants based on user answers
    const scoredGrants = allGrants.map(grant => ({
      ...grant,
      matchScore: calculateMatchScore(grant, answers)
    }));

    const currentDate = new Date();

    // Sort by match score, filter out low matches and expired grants
    return scoredGrants
      .filter(grant => {
        const deadlineDate = new Date(grant.deadline);
        return grant.matchScore > 30 && deadlineDate > currentDate;
      })
      .sort((a, b) => b.matchScore - a.matchScore);
  } catch (error) {
    console.error('Error fetching grants:', error);
    return [];
  }
}

function calculateMatchScore(grant: Grant, answers: UserAnswers): number {
  let score = 0;
  const maxScore = 100;

  // State match (highest priority)
  if (answers.state && grant.eligibility.toLowerCase().includes(answers.state.toLowerCase())) {
    score += 30;
  }

  // Organization type match
  if (answers.organizationType) {
    if (
      (answers.organizationType === 'nonprofit' && 
       grant.eligibility.toLowerCase().includes('non-profit')) ||
      (answers.organizationType === 'smallBusiness' && 
       grant.eligibility.toLowerCase().includes('business')) ||
      (answers.organizationType === 'individual' && 
       grant.eligibility.toLowerCase().includes('individual')) ||
      (answers.organizationType === 'government' && 
       grant.eligibility.toLowerCase().includes('government'))
    ) {
      score += 30;
    }
  }

  // Demographics match
  if (answers.demographics && Array.isArray(answers.demographics)) {
    const demographics = answers.demographics as string[];
    if (demographics.length > 0 && demographics[0] !== 'none') {
      const hasMatch = demographics.some(demo => {
        const demoTerms: { [key: string]: string[] } = {
          'minority': ['minority', 'bipoc', 'diverse'],
          'women': ['women', 'female'],
          'veteran': ['veteran', 'military'],
          'lgbtq': ['lgbtq', 'lgbt', 'lgbtqia'],
          'disability': ['disability', 'disabled', 'abilities']
        };
        
        const terms = demoTerms[demo] || [demo];
        return terms.some(term => 
          grant.eligibility.toLowerCase().includes(term) || 
          grant.description.toLowerCase().includes(term)
        );
      });
      
      if (hasMatch) score += 20;
    }
  }

  // Focus area match
  if (answers.primaryFocus) {
    if (grant.category.toLowerCase().includes(answers.primaryFocus.toLowerCase())) {
      score += 20;
    }
  }

  // Grant amount match
  if (answers.grantAmount && Array.isArray(answers.grantAmount)) {
    const amounts = answers.grantAmount as string[];
    const matchesAmount = amounts.some(amount => {
      switch (amount) {
        case 'under10k': return grant.amount < 10000;
        case '10kTo50k': return grant.amount >= 10000 && grant.amount <= 50000;
        case '50kTo100k': return grant.amount > 50000 && grant.amount <= 100000;
        case 'over100k': return grant.amount > 100000;
        default: return false;
      }
    });
    if (matchesAmount) score += 20;
  }

  return Math.min(score, maxScore);
}